import React from 'react';
// Using AnalyticsContext directly to pass source context to AVP components (ContextualAnalyticsData doesn't support custom source prop)
import { useAvpAtlassianUser } from '@jira/platform__avp-config/src/useAvpAtlassianUser.tsx';
import { useAvpWorkspace } from '@jira/platform__avp-config/src/useAvpWorkspace.tsx';
import { useIsOrgAdmin } from '@jira/platform__services-is-org-admin/src/is-org-admin.tsx';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { fg } from '@atlassian/jira-feature-gating';
import { ENTRY_POINT_ID_JSM_ASSETS_REPORTING } from '@atlassian/jira-feedback-collector/src/constants.tsx';

import { useIntl } from '@atlassian/jira-intl';
import { usePathParam, Redirect, useRouterActions } from '@atlassian/react-resource-router';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRovoEntitlementResource } from '@jira/router-resources__rovo-entitlement/src/useRovoEntitlementResource.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import type { AssetsReportsV2Props } from './AssetsReportsV2Props.types.tsx';
import AssetsDeeplinkingModal from './AssetsDeeplinkingModal.tsx';
import FiltersOnboardingModal from './FiltersOnboardingModal.tsx';
import { useAssetsDashboardsSemanticModel } from '@jira/router-resources__viz-platform/src/assetsDashboardsSemanticModelResource.tsx';

const isUuid = (input: string): boolean =>
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input);

const LazyITSMAnalyticsDashboard = lazyForPaint(() =>
	import(/* webpackChunkName: "avp-viz-platform-views" */ '@atlassian/viz-platform-views').then(
		({ ITSMAnalyticsDashboard }) => ITSMAnalyticsDashboard,
	),
);

const fallbackFormDimensions = {
	viewMode: {
		width: 592,
		maxHeight: 455,
		minHeight: 240,
	},
	editMode: {
		width: 592,
		maxHeight: 622,
		minHeight: 407,
	},
};

export const DashboardEditable = ({ workspaceId }: AssetsReportsV2Props): React.JSX.Element => {
	const [templateId] = usePathParam('templateId');
	const cloudId = useCloudId();
	const { locale } = useIntl();
	const atlassianUser = useAvpAtlassianUser();
	const workspace = useAvpWorkspace(workspaceId);
	const environment = useEnvironment();
	const { isOrgAdmin } = useIsOrgAdmin();
	const { data: rovoEntitlement } = useRovoEntitlementResource();
	const { replace } = useRouterActions();

	if (!templateId) {
		return <Redirect to="/jira/servicedesk/assets/reports" />;
	}

	const content = (
		<>
			<AssetsDeeplinkingModal />
			{fg('platform_avp_enable_jsm_assets_metrics') && <FiltersOnboardingModal />}
			<Box xcss={dashboardEditableWrapper}>
				<LazyITSMAnalyticsDashboard
					hostingProduct="jsm-cmdb"
					// dashboardId is a uuid, slug is not
					slug={isUuid(templateId) ? null : templateId}
					dashboardId={isUuid(templateId) ? templateId : null}
					activationId={workspaceId}
					services={{
						useSemanticModel: fg('platform_avp_enable_jsm_assets_metrics')
							? useAssetsDashboardsSemanticModel
							: undefined,
					}}
					allowGlobalFilters
					cloudId={cloudId}
					locale={locale}
					atlassianUser={atlassianUser}
					{...(fg('enable_global_filter_config_ers_version') && { fallbackFormDimensions })}
					workspace={workspace}
					isUserOrgAdmin={isOrgAdmin}
					environment={environment}
					perimeter="commercial"
					location="assets"
					aiConfig={{
						rovoEnabled:
							fg('jsm_assets_dashboards_ai_insights') &&
							Boolean(rovoEntitlement?.isRovoEnabled && rovoEntitlement?.isRovoLLMEnabled),
						feedbackEntrypointId: ENTRY_POINT_ID_JSM_ASSETS_REPORTING,
					}}
					{...(fg('jsm_reporting_graphql_fe_changes') && {
						redirectToDashboardId: (dashboardId) => {
							replace(`/jira/assets/dashboards/${dashboardId}`);
						},
					})}
					key={templateId}
				/>
			</Box>
		</>
	);

	if (fg('jsm_assets_analytics_context_migration')) {
		return <AnalyticsContext data={{ source: 'assets-reporting' }}>{content}</AnalyticsContext>;
	}

	return content;
};

const dashboardEditableWrapper = xcss({
	marginLeft: 'space.400',
	marginRight: 'space.400',
});
